import React, { useState, useEffect } from 'react';
import './EditContactModal.css';
import { MdClose, MdAdd, MdDelete, MdEmail, MdPhone, MdLocationOn, MdPerson, MdSearch } from 'react-icons/md';
import { getToken } from '../../../../../services/api';

const EditContactModal = ({ isOpen, onClose, precatorio, state, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [contactData, setContactData] = useState({
    emails: [],
    phones: [],
    address: '',
    cidade: '',
    uf: '',
    data_nascimento: ''
  });
  const [searchDocument, setSearchDocument] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchMessage, setSearchMessage] = useState(null);
  const [searchResult, setSearchResult] = useState(null);
  const [searchType, setSearchType] = useState(null);

  useEffect(() => {
    if (isOpen && precatorio) {
      console.log('EditContactModal opened with precatorio:', precatorio);
      console.log('State prop:', state);
      fetchContactData();
    }
  }, [isOpen, precatorio, state]);

  const fetchContactData = async () => {
    // Reset DirectData state to prevent showing stale data from previous modal
    setSearchResult(null);
    setSearchType(null);

    try {
      const token = getToken();
      const API_URL = process.env.NODE_ENV === 'production'
        ? process.env.REACT_APP_VERCEL_API_URL
        : process.env.REACT_APP_LOCAL_API_URL;

      // Set default contact data from precatorio first
      setContactData({
        emails: precatorio.emails_contato || [],
        phones: precatorio.telefones_contato || [],
        address: '',
        cidade: precatorio.cidade || '',
        uf: precatorio.uf || '',
        data_nascimento: safeParseDateToBrazilian(precatorio.data_nascimento)
      });

      // Only fetch from documents API if document_type_used exists
      if (precatorio.document_type_used && precatorio.document_used) {
        try {
          const fetchUrl = `${API_URL}/documents/${precatorio.document_type_used}/${state || precatorio.uf}/${precatorio.document_used}/contact`;
          const response = await fetch(fetchUrl, {
            headers: { 'Authorization': `Bearer ${token}` }
          });

          if (response.ok) {
            const data = await response.json();
            setContactData({
              emails: data.data.emails || [],
              phones: data.data.phones || [],
              address: data.data.address || '',
              cidade: data.data.cidade || '',
              uf: data.data.uf || precatorio.uf || '',
              data_nascimento: safeParseDateToBrazilian(precatorio.data_nascimento)
            });
          }
        } catch (docErr) {
          console.error('Error fetching document data:', docErr);
        }
      }

      // ALWAYS fetch cached DirectData info (moved outside the if block)
      try {
        const ddResponse = await fetch(
          `${API_URL}/contacted-precatorios/${state || precatorio.uf}/${precatorio._id}/directdata-info`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (ddResponse.ok) {
          const ddData = await ddResponse.json();
          if (ddData.data) {
            setSearchResult(ddData.data);
            setSearchType(ddData.type);
          }
        }
      } catch (ddErr) {
        console.error('Error fetching DirectData info:', ddErr);
      }
    } catch (err) {
      console.error('Error in fetchContactData:', err);
    }
  };

  const handleAddEmail = () => {
    setContactData(prev => ({
      ...prev,
      emails: [...prev.emails, '']
    }));
  };

  const handleRemoveEmail = (index) => {
    setContactData(prev => ({
      ...prev,
      emails: prev.emails.filter((_, i) => i !== index)
    }));
  };

  const handleEmailChange = (index, value) => {
    setContactData(prev => ({
      ...prev,
      emails: prev.emails.map((email, i) => i === index ? value : email)
    }));
  };

  const handleAddPhone = () => {
    setContactData(prev => ({
      ...prev,
      phones: [...prev.phones, '']
    }));
  };

  const handleRemovePhone = (index) => {
    setContactData(prev => ({
      ...prev,
      phones: prev.phones.filter((_, i) => i !== index)
    }));
  };

  const handlePhoneChange = (index, value) => {
    // Remove non-numeric characters
    const cleaned = value.replace(/\D/g, '');
    
    // Apply Brazilian phone formatting
    let formatted = cleaned;
    if (cleaned.length <= 11) {
      if (cleaned.length === 11) {
        // Mobile: (XX) 9XXXX-XXXX
        formatted = cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
      } else if (cleaned.length === 10) {
        // Landline: (XX) XXXX-XXXX
        formatted = cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
      } else if (cleaned.length > 6) {
        // Partial formatting
        if (cleaned.length >= 7) {
          formatted = cleaned.replace(/(\d{2})(\d{4,5})(\d{0,4})/, '($1) $2-$3');
        } else {
          formatted = cleaned.replace(/(\d{2})(\d+)/, '($1) $2');
        }
      } else if (cleaned.length > 2) {
        formatted = cleaned.replace(/(\d{2})(\d+)/, '($1) $2');
      }
    }
    
    setContactData(prev => ({
      ...prev,
      phones: prev.phones.map((phone, i) => i === index ? formatted : phone)
    }));
  };;

  const formatPhone = (phone) => {
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11) {
      return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    } else if (cleaned.length === 10) {
      return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }
    return phone;
  };

  const formatDateToBrazilian = (isoDate) => {
    if (!isoDate) return '';
    const date = new Date(isoDate + 'T00:00:00');
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatDateToISO = (brazilianDate) => {
    if (!brazilianDate) return '';
    const parts = brazilianDate.split('/');
    if (parts.length !== 3) return '';
    const [day, month, year] = parts;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  };

  const safeParseDateToBrazilian = (dateValue) => {
    if (!dateValue) return '';

    // If already in Brazilian format (DD/MM/YYYY), return as-is
    if (typeof dateValue === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(dateValue)) {
      return dateValue;
    }

    try {
      const date = new Date(dateValue);
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return '';
      }
      return formatDateToBrazilian(date.toISOString().split('T')[0]);
    } catch (e) {
      console.error('Error parsing date:', dateValue, e);
      return '';
    }
  };

  const handleDateChange = (value) => {
    const cleaned = value.replace(/\D/g, '');
    let formatted = cleaned;
    if (cleaned.length >= 2) {
      formatted = cleaned.slice(0, 2);
      if (cleaned.length >= 3) {
        formatted += '/' + cleaned.slice(2, 4);
      }
      if (cleaned.length >= 5) {
        formatted += '/' + cleaned.slice(4, 8);
      }
    }
    setContactData(prev => ({ ...prev, data_nascimento: formatted }));
  };

  const formatCpfCnpj = (value) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 11) {
      return cleaned
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    } else {
      return cleaned
        .slice(0, 14)
        .replace(/(\d{2})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1/$2')
        .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
    }
  };

  const handleSearchDocumentChange = (value) => {
    setSearchDocument(formatCpfCnpj(value));
    setSearchMessage(null);
  };

  const handleSearch = async () => {
    const cleanValue = searchDocument.replace(/\D/g, '');

    if (cleanValue.length !== 11 && cleanValue.length !== 14) {
      setSearchMessage({ type: 'error', text: 'CPF deve ter 11 dígitos ou CNPJ deve ter 14 dígitos' });
      return;
    }

    setSearchLoading(true);
    setSearchMessage(null);

    try {
      const token = getToken();
      const API_URL = process.env.NODE_ENV === 'production'
        ? process.env.REACT_APP_VERCEL_API_URL
        : process.env.REACT_APP_LOCAL_API_URL;

      const endpoint = cleanValue.length === 11
        ? `${API_URL}/pessoa-fisica/${cleanValue}`
        : `${API_URL}/pessoa-juridica/${cleanValue}`;

      const response = await fetch(endpoint, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        setSearchMessage({ type: 'error', text: result.error || 'Dados não encontrados' });
        return;
      }

      const data = result.data;
      const isCpf = cleanValue.length === 11;
      setSearchResult(data);
      setSearchType(isCpf ? 'cpf' : 'cnpj');

      // Immediately save searched_cpf_cnpj to the precatorio
      try {
        await fetch(
          `${API_URL}/contacted-precatorios/${state}/${precatorio._id}`,
          {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ searched_cpf_cnpj: cleanValue })
          }
        );
      } catch (saveErr) {
        console.error('Error saving searched_cpf_cnpj:', saveErr);
      }

      // Auto-populate fields
      const newEmails = (data.emails || [])
        .map(e => e.enderecoEmail)
        .filter(Boolean);

      const newPhones = (data.telefones || [])
        .map(t => {
          const num = (t.telefoneComDDD || '').replace(/\D/g, '');
          if (num.length === 11) return num.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
          if (num.length === 10) return num.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
          return t.telefoneComDDD || '';
        })
        .filter(Boolean);

      const firstEndereco = (data.enderecos || [])[0];
      let newAddress = '';
      let newCidade = '';
      let newUf = '';
      if (firstEndereco) {
        const parts = [firstEndereco.logradouro, firstEndereco.numero, firstEndereco.complemento].filter(Boolean);
        newAddress = parts.join(', ');
        newCidade = firstEndereco.cidade || '';
        newUf = firstEndereco.uf || '';
      }

      setContactData(prev => ({
        ...prev,
        emails: newEmails.length > 0 ? newEmails : prev.emails,
        phones: newPhones.length > 0 ? newPhones : prev.phones,
        address: newAddress || prev.address,
        cidade: newCidade || prev.cidade,
        uf: newUf || prev.uf,
        data_nascimento: safeParseDateToBrazilian(data.dataNascimento) || prev.data_nascimento
      }));

      const sourceLabel = result.source === 'database' ? 'banco de dados' : 'API DirectData';
      setSearchMessage({ type: 'success', text: `Dados encontrados (${sourceLabel})` });

    } catch (err) {
      console.error('Search error:', err);
      setSearchMessage({ type: 'error', text: 'Erro ao buscar dados. Tente novamente.' });
    } finally {
      setSearchLoading(false);
    }
  };

  const isValidDate = (dateString) => {
    if (!dateString) return true;
    const parts = dateString.split('/');
    if (parts.length !== 3) return false;
    const [day, month, year] = parts.map(p => parseInt(p, 10));
    if (isNaN(day) || isNaN(month) || isNaN(year)) return false;
    if (day < 1 || day > 31) return false;
    if (month < 1 || month > 12) return false;
    if (year < 1900 || year > new Date().getFullYear()) return false;
    const date = new Date(year, month - 1, day);
    return date.getDate() === day && date.getMonth() === month - 1 && date.getFullYear() === year;
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = getToken();
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_VERCEL_API_URL 
        : process.env.REACT_APP_LOCAL_API_URL;

      // Filter out empty emails and phones, and clean phone format
      const cleanedData = {
        ...contactData,
        emails: contactData.emails.filter(email => email.trim()),
        phones: contactData.phones
          .filter(phone => phone.trim())
          .map(phone => {
            // Remove all non-numeric characters for backend
            return phone.replace(/[^0-9]/g, '');
          })
          .filter(phone => phone.length === 10 || phone.length === 11)
      };

      const endpoint = `${API_URL}/documents/${precatorio.document_type_used.toLowerCase()}/${state || precatorio.uf}/${precatorio.document_used}/contact`;
      console.log('Save endpoint:', endpoint);
      console.log('Saving data:', cleanedData);
      
      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(cleanedData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao atualizar contato');
      }

      // Also update the ContactedPrecatorio with data_nascimento, telefones_contato, and emails_contato
      const precatorioUpdateEndpoint = `${API_URL}/contacted-precatorios/${state}/${precatorio._id}`;
      const precatorioUpdateData = {
        data_nascimento: contactData.data_nascimento ? formatDateToISO(contactData.data_nascimento) : null,
        telefones_contato: cleanedData.phones,
        emails_contato: cleanedData.emails,
        ...(searchResult ? { searched_cpf_cnpj: searchDocument.replace(/\D/g, '') } : {})
      };

      const precatorioResponse = await fetch(precatorioUpdateEndpoint, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(precatorioUpdateData)
      });

      if (!precatorioResponse.ok) {
        console.error('Failed to update precatorio data, but document contact was updated');
      }

      // Call success callback
      if (onSuccess) {
        onSuccess();
      }

      // Close modal
      onClose();
      
      // Show success message
      alert('Informações de contato atualizadas com sucesso!');

    } catch (err) {
      setError(err.message || 'Erro ao salvar informações');
      console.error('Save error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const brazilianStates = [
    'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
    'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
    'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
  ];

  return (
    <div className="edit-contact-modal-overlay" onClick={onClose}>
      <div className="edit-contact-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Editar Informações de Contato</h2>
          <button className="close-button" onClick={onClose}>
            <MdClose />
          </button>
        </div>

        <div className="modal-body">
          <div className="document-info">
            <span className="document-label">Documento:</span>
            <span className="document-value">
              {precatorio.document_type_used} - {precatorio.document_used}
            </span>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="form-section search-section">
            <div className="section-header">
              <MdSearch className="section-icon" />
              <h3>Buscar por CPF ou CNPJ</h3>
            </div>
            <div className="search-row">
              <input
                type="text"
                value={searchDocument}
                onChange={(e) => handleSearchDocumentChange(e.target.value)}
                placeholder="Digite o CPF ou CNPJ"
                maxLength="18"
                disabled={loading || searchLoading}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
              />
              <button
                type="button"
                className="search-button"
                onClick={handleSearch}
                disabled={loading || searchLoading || !searchDocument.trim()}
              >
                {searchLoading ? 'Buscando...' : <><MdSearch /> Buscar</>}
              </button>
            </div>
            {searchMessage && (
              <div className={`search-message ${searchMessage.type}`}>
                {searchMessage.text}
              </div>
            )}
          </div>

          {searchResult && searchType === 'cpf' && (
            <div className="search-result-card">
              <h4>Dados da Pessoa Física</h4>
              <div className="search-result-grid">
                {searchResult.nome && <><span className="search-result-label">Nome</span><span className="search-result-value">{searchResult.nome}</span></>}
                {searchResult.sexo && <><span className="search-result-label">Sexo</span><span className="search-result-value">{searchResult.sexo}</span></>}
                {searchResult.dataNascimento && <><span className="search-result-label">Data de Nascimento</span><span className="search-result-value">{searchResult.dataNascimento}</span></>}
                {searchResult.nomeMae && <><span className="search-result-label">Nome da Mãe</span><span className="search-result-value">{searchResult.nomeMae}</span></>}
                {searchResult.idade != null && <><span className="search-result-label">Idade</span><span className="search-result-value">{searchResult.idade}</span></>}
                {searchResult.signo && <><span className="search-result-label">Signo</span><span className="search-result-value">{searchResult.signo}</span></>}
                {searchResult.rendaEstimada && <><span className="search-result-label">Renda Estimada</span><span className="search-result-value">{searchResult.rendaEstimada}</span></>}
                {searchResult.rendaFaixaSalarial && <><span className="search-result-label">Faixa Salarial</span><span className="search-result-value">{searchResult.rendaFaixaSalarial}</span></>}
              </div>
              {searchResult.enderecos && searchResult.enderecos.length > 0 && (
                <div className="search-result-addresses">
                  <h5>Endereços ({searchResult.enderecos.length})</h5>
                  {searchResult.enderecos.map((end, idx) => (
                    <div key={idx} className="address-item">
                      {[end.logradouro, end.numero, end.complemento].filter(Boolean).join(', ')} — {end.bairro}, {end.cidade}/{end.uf} - CEP: {end.cep}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {searchResult && searchType === 'cnpj' && (
            <div className="search-result-card">
              <h4>Dados da Pessoa Jurídica</h4>
              <div className="search-result-grid">
                {searchResult.razaoSocial && <><span className="search-result-label">Razão Social</span><span className="search-result-value">{searchResult.razaoSocial}</span></>}
                {searchResult.nomeFantasia && <><span className="search-result-label">Nome Fantasia</span><span className="search-result-value">{searchResult.nomeFantasia}</span></>}
                {searchResult.dataFundacao && <><span className="search-result-label">Data de Fundação</span><span className="search-result-value">{searchResult.dataFundacao}</span></>}
                {searchResult.cnaeDescricao && <><span className="search-result-label">CNAE</span><span className="search-result-value">{searchResult.cnaeDescricao}</span></>}
                {searchResult.situacaoCadastral && <><span className="search-result-label">Situação Cadastral</span><span className="search-result-value">{searchResult.situacaoCadastral}</span></>}
                {searchResult.situacaoEspecial && <><span className="search-result-label">Situação Especial</span><span className="search-result-value">{searchResult.situacaoEspecial}</span></>}
                {searchResult.naturezaJuridicaDescricao && <><span className="search-result-label">Natureza Jurídica</span><span className="search-result-value">{searchResult.naturezaJuridicaDescricao}</span></>}
                {searchResult.porte && <><span className="search-result-label">Porte</span><span className="search-result-value">{searchResult.porte}</span></>}
                {searchResult.faixaFuncionarios && <><span className="search-result-label">Faixa de Funcionários</span><span className="search-result-value">{searchResult.faixaFuncionarios}</span></>}
                {searchResult.quantidadeFuncionarios != null && <><span className="search-result-label">Qtd. Funcionários</span><span className="search-result-value">{searchResult.quantidadeFuncionarios}</span></>}
                {searchResult.faixaFaturamento && <><span className="search-result-label">Faixa de Faturamento</span><span className="search-result-value">{searchResult.faixaFaturamento}</span></>}
                {searchResult.faturamentoPresumido && <><span className="search-result-label">Faturamento Presumido</span><span className="search-result-value">{searchResult.faturamentoPresumido}</span></>}
                {searchResult.tributacao && <><span className="search-result-label">Tributação</span><span className="search-result-value">{searchResult.tributacao}</span></>}
                {searchResult.opcaoMEI && <><span className="search-result-label">Opção MEI</span><span className="search-result-value">{searchResult.opcaoMEI}</span></>}
                {searchResult.opcaoSimples && <><span className="search-result-label">Opção Simples</span><span className="search-result-value">{searchResult.opcaoSimples}</span></>}
                {searchResult.quantidadeFiliais && <><span className="search-result-label">Filiais</span><span className="search-result-value">{searchResult.quantidadeFiliais}</span></>}
                {searchResult.ramo && <><span className="search-result-label">Ramo</span><span className="search-result-value">{searchResult.ramo}</span></>}
                {searchResult.tipoEmpresa && <><span className="search-result-label">Tipo Empresa</span><span className="search-result-value">{searchResult.tipoEmpresa}</span></>}
                {searchResult.ultimaAtualizacaoPJ && <><span className="search-result-label">Última Atualização</span><span className="search-result-value">{searchResult.ultimaAtualizacaoPJ}</span></>}
              </div>
              {searchResult.enderecos && searchResult.enderecos.length > 0 && (
                <div className="search-result-addresses">
                  <h5>Endereços ({searchResult.enderecos.length})</h5>
                  {searchResult.enderecos.map((end, idx) => (
                    <div key={idx} className="address-item">
                      {[end.logradouro, end.numero, end.complemento].filter(Boolean).join(', ')} — {end.bairro}, {end.cidade}/{end.uf} - CEP: {end.cep}
                    </div>
                  ))}
                </div>
              )}
              {searchResult.socios && searchResult.socios.length > 0 && (
                <div className="search-result-socios">
                  <h5>Sócios ({searchResult.socios.length})</h5>
                  <table className="socios-table">
                    <thead>
                      <tr>
                        <th>Nome</th>
                        <th>Documento</th>
                        <th>Cargo</th>
                        <th>Entrada</th>
                      </tr>
                    </thead>
                    <tbody>
                      {searchResult.socios.map((socio, idx) => (
                        <tr key={idx}>
                          <td>{socio.nome}</td>
                          <td>{socio.documento}</td>
                          <td>{socio.cargo}</td>
                          <td>{socio.dataEntrada}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          <div className="form-section">
            <div className="section-header">
              <MdEmail className="section-icon" />
              <h3>Emails</h3>
              <button 
                type="button" 
                className="add-button" 
                onClick={handleAddEmail}
                disabled={loading}
              >
                <MdAdd /> Adicionar
              </button>
            </div>
            <div className="emails-list">
              {contactData.emails.length === 0 ? (
                <p className="empty-message">Nenhum email cadastrado</p>
              ) : (
                contactData.emails.map((email, index) => (
                  <div key={index} className="input-row">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => handleEmailChange(index, e.target.value)}
                      placeholder="exemplo@email.com"
                      disabled={loading}
                    />
                    <button 
                      type="button"
                      className="remove-button"
                      onClick={() => handleRemoveEmail(index)}
                      disabled={loading}
                    >
                      <MdDelete />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="form-section">
            <div className="section-header">
              <MdPhone className="section-icon" />
              <h3>Telefones</h3>
              <button 
                type="button" 
                className="add-button" 
                onClick={handleAddPhone}
                disabled={loading}
              >
                <MdAdd /> Adicionar
              </button>
            </div>
            <div className="phones-list">
              {contactData.phones.length === 0 ? (
                <p className="empty-message">Nenhum telefone cadastrado</p>
              ) : (
                contactData.phones.map((phone, index) => {
                  const cleaned = phone.replace(/[^0-9]/g, '');
                  const isValid = cleaned.length === 10 || cleaned.length === 11;
                  
                  return (
                  <div key={index} className="input-row">
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => handlePhoneChange(index, e.target.value)}
                      placeholder="(XX) XXXXX-XXXX"
                      maxLength="15"
                      style={{ borderColor: phone && !isValid ? '#ff0000' : undefined }}
                      title={!isValid && phone ? 'Telefone deve ter 10 ou 11 dígitos' : ''}
                      disabled={loading}
                    />
                    {phone && !isValid && (
                      <span style={{ color: '#ff0000', fontSize: '12px', marginLeft: '5px' }}>
                        Inválido
                      </span>
                    )}
                    <button 
                      type="button"
                      className="remove-button"
                      onClick={() => handleRemovePhone(index)}
                      disabled={loading}
                    >
                      <MdDelete />
                    </button>
                  </div>
                  );
                })
              )}
            </div>
          </div>

          <div className="form-section">
            <div className="section-header">
              <MdLocationOn className="section-icon" />
              <h3>Endereço</h3>
            </div>
            <div className="address-fields">
              <div className="form-group full-width">
                <label>Endereço</label>
                <input
                  type="text"
                  value={contactData.address}
                  onChange={(e) => setContactData(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="Rua, número, complemento..."
                  disabled={loading}
                />
              </div>
              <div className="form-group">
                <label>Cidade</label>
                <input
                  type="text"
                  value={contactData.cidade}
                  onChange={(e) => setContactData(prev => ({ ...prev, cidade: e.target.value }))}
                  placeholder="Cidade"
                  disabled={loading}
                />
              </div>
              <div className="form-group small">
                <label>UF</label>
                <select
                  value={contactData.uf}
                  onChange={(e) => setContactData(prev => ({ ...prev, uf: e.target.value }))}
                  disabled={loading}
                >
                  <option value="">--</option>
                  {brazilianStates.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Data de Nascimento field - only for CPF */}
          {precatorio.tipo_documento === 'CPF' && (
            <div className="form-section">
              <div className="section-header">
                <MdPerson className="section-icon" />
                <h3>Data de Nascimento</h3>
              </div>
              <div className="form-group">
                <label>Data de Nascimento (necessário para Certidão Federal)</label>
                <input
                  type="text"
                  value={contactData.data_nascimento}
                  onChange={(e) => handleDateChange(e.target.value)}
                  placeholder="DD/MM/AAAA"
                  maxLength="10"
                  disabled={loading}
                  style={{ 
                    borderColor: contactData.data_nascimento && !isValidDate(contactData.data_nascimento) ? '#ff0000' : undefined 
                  }}
                />
                {contactData.data_nascimento && !isValidDate(contactData.data_nascimento) && (
                  <span style={{ color: '#ff0000', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    Data inválida. Use o formato DD/MM/AAAA
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button 
            className="cancel-button" 
            onClick={onClose}
            disabled={loading}
          >
            Cancelar
          </button>
          <button 
            className="save-button" 
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditContactModal;